import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./AddSpeaker.css";

function AddSpeaker() {
  const [formData, setFormData] = useState({
    name: "",
    university: "",
    position: "",
    website: "",
    type: "",
    title: "", // Talk title (optional)
    abstract: "", // Abstract (optional)
  });
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (limit to 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (selectedFile.size > maxSize) {
        Swal.fire({
          icon: 'warning',
          title: 'File Too Large',
          text: 'Please select an image smaller than 5MB',
          confirmButtonColor: '#5e71f6',
        });
        e.target.value = ''; // Clear the file input
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const validateForm = () => {
    const { name, university, position, type } = formData;

    if (!name.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please enter the speaker name',
        confirmButtonColor: '#5e71f6',
      });
      return false;
    }

    if (!university.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please enter the university/organization',
        confirmButtonColor: '#5e71f6',
      });
      return false;
    }

    if (!position.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please enter the position/title',
        confirmButtonColor: '#5e71f6',
      });
      return false;
    }

    if (!type) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please select the speaker type',
        confirmButtonColor: '#5e71f6',
      });
      return false;
    }

    // Photo and other fields are optional now

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const data = new FormData();
    data.append("title", formData.title);
    data.append("name", formData.name);
    data.append("position", formData.position);
    data.append("university", formData.university);
    data.append("abstract", formData.abstract);
    data.append("website", formData.website);
    data.append("type", formData.type);
    if (file) {
      data.append("picture", file);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/speaker`,
        data
      );
      if (response.status === 201) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Speaker added successfully!",
          confirmButtonColor: '#5e71f6',
        });

        // Reset the form fields after successful submission
        setFormData({
          name: "",
          university: "",
          position: "",
          website: "",
          type: "",
          title: "",
          abstract: "",
        });
        setFile(null);
        setFileName("");
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
      } else {
        throw new Error("Failed to add speaker");
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.response?.data?.message || "Failed to add speaker. Please try again.",
        confirmButtonColor: '#5e71f6',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-speaker-wrapper">
      <div className="add-speaker-container">
        {/* Header Section */}
        <div className="add-speaker-header">
          <h1 className="add-speaker-title">Add Speaker</h1>
          <p className="add-speaker-subtitle">Add keynote speakers and invited talks</p>
        </div>

        {/* Form Card */}
        <div className="speaker-form-card">
          <div className="speaker-form-header">
            <h2 className="form-card-title">
              <svg className="form-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Speaker Information
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="speaker-form">
            {/* Name Field - Now First */}
            <div className="form-field">
              <label className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Speaker Name <span className="required-mark">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter speaker's full name"
                className="form-input"
              />
            </div>

            {/* University Field - Now Second */}
            <div className="form-field">
              <label className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                University/Organization <span className="required-mark">*</span>
              </label>
              <input
                type="text"
                name="university"
                value={formData.university}
                onChange={handleChange}
                placeholder="Enter university or organization name"
                className="form-input"
              />
            </div>

            {/* Position Field - Now Third */}
            <div className="form-field">
              <label className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Position/Title <span className="required-mark">*</span>
              </label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleChange}
                placeholder="e.g., Professor, Research Scientist, Dr."
                className="form-input"
              />
            </div>

            {/* Website Field - Optional */}
            <div className="form-field">
              <label className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                Website <span className="optional-mark">(Optional)</span>
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://example.com"
                className="form-input"
              />
            </div>

            {/* File Upload Field - Optional */}
            <div className="form-field">
              <label className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Speaker Photo <span className="optional-mark">(Optional, max 5MB)</span>
              </label>
              <div className="file-upload-container">
                <label className="file-upload-label">
                  <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="upload-text">
                    {fileName ? fileName : "Choose a file or drag it here"}
                  </span>
                  <input 
                    type="file" 
                    name="picture" 
                    onChange={handleFileChange}
                    accept="image/*"
                    className="file-input"
                  />
                </label>
                {fileName && (
                  <div className="file-selected-indicator">
                    <svg className="check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    File selected successfully
                  </div>
                )}
              </div>
            </div>

            {/* Type Field */}
            <div className="form-field">
              <label className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Speaker Type <span className="required-mark">*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select speaker type</option>
                <option value="KeynoteSpeaker">Keynote Speaker</option>
                <option value="InvitedTalks">Invited Talks</option>
              </select>
            </div>

            {/* Talk Title Field - Optional */}
            <div className="form-field">
              <label className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Talk Title <span className="optional-mark">(Optional)</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter the title of the talk or presentation"
                className="form-input"
              />
            </div>

            {/* Abstract Field - Optional */}
            <div className="form-field">
              <label className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Abstract <span className="optional-mark">(Optional)</span>
              </label>
              <textarea
                name="abstract"
                value={formData.abstract}
                onChange={handleChange}
                placeholder="Enter the abstract, biography, or description of the speaker's talk"
                className="form-textarea"
                rows="5"
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="button-icon spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Speaker
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddSpeaker;
